 "use client";

import Section from "@/components/section";
import { Button, buttonVariants } from "@/components/ui/button";
import { motion } from "src/lib/motion";
import { useState, useEffect, useRef } from "react";
import AIConsultingTeam from "@/components/sections/ai-consulting-team";
import { Check, Brain, LineChart, FileText, Users, Building2, Hospital, ShoppingBag, Factory, Truck, CircleGauge, BookOpenCheck } from "lucide-react";
import { Icons } from "@/components/icons";
import BlurFade from "@/components/magicui/blur-fade";
import DotPattern from "@/components/magicui/dot-pattern";
import Ripple from "@/components/magicui/ripple";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TabSwitchSection from "@/components/ui/tab-switch-section";
import ServicesSection from "@/components/sections/services";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useTranslations } from "@/lib/hooks/use-translations";
import { useParams } from "next/navigation";
import { Locale } from "@/lib/i18n";

const ease = [0.16, 1, 0.3, 1];

// Import Bilder
import aiConsultingImage from "@/assets/images/ai-consulting.svg";
import businessDiscussionImage from "@/assets/images/business-discussion.jpg";
import scientistImage from "@/assets/images/scientist-working-laboratory-analysing-test-trial-vaccine-checking-drug-data-standing-front-pc.jpg";
import smartphoneOfficeImage from "@/assets/images/smartphone-office.jpg";
import productionLineImage from "@/assets/images/photo-automobile-production-line-welding-car-body-modern-car-assembly-plant-auto-industry.jpg";
import logisticsImage from "@/assets/images/technological-futuristic-holograms-logistics-means-transport.jpg";
import dashboardImage from "@/assets/images/dashboard.png";

function Hero() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <Section
      id="hero"
      className="relative overflow-hidden bg-neutral-100 dark:bg-neutral-900 py-12 sm:py-24"
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-foreground">
                {t('pages.solutions.ai-consulting.hero.title')}
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mt-4">
                {t('pages.solutions.ai-consulting.hero.subtitle')}
              </h2>
              <p className="text-xl text-muted-foreground">
                {t('pages.solutions.ai-consulting.hero.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease }}
              className="flex flex-col sm:flex-row w-full gap-4 mt-6 lg:justify-start sm:justify-center"
            >
              <Link
                href={`/${locale}/contact`}
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "w-full sm:w-auto text-background flex gap-2"
                )}
              >
                <Icons.logoIcon className="h-6 w-6" />
                {t('pages.solutions.ai-consulting.hero.cta.primary')}
              </Link>
              <button
                onClick={() => document.getElementById("why-ai-consulting")?.scrollIntoView({ behavior: "smooth" })}
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full sm:w-auto"
                )}
              >
                {t('pages.solutions.ai-consulting.hero.cta.secondary')}
              </button>
            </motion.div>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="relative order-2"
          >
            <DotPattern
              className="absolute inset-0"
              width={40}
              height={40}
              radius={1.5}
              offset={20}
              color="var(--primary)"
              opacity={0.1}
            />
            <div className="w-full h-[250px] sm:h-[400px] rounded-lg relative z-10 bg-neutral-100 dark:bg-neutral-900">
              <Image
                src={aiConsultingImage.src}
                alt="AI Consulting Services - Strategic implementation of artificial intelligence solutions for business transformation"
                className="w-auto h-full object-contain mix-blend-multiply dark:mix-blend-screen"
              priority
                width={800}
                height={600}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
const SWIPE_THRESHOLD = 50;
function WhyAIConsulting() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;
  const [percentage, setPercentage] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout>();
  const totalSlides = 5;
  const startX = useRef(0);
  const endX = useRef(0);

  useEffect(() => {
    if (isInView && percentage < 73) {
      intervalRef.current = setInterval(() => {
        setPercentage(prev => {
          if (prev >= 73) {
            clearInterval(intervalRef.current);
            return 73;
          }
          return Math.min(73, prev + Math.ceil((73 - prev) / 15));
        });
      }, 40);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isInView, percentage]);

  const data = [
    {
      id: 1,
      title: t('pages.solutions.ai-consulting.why.benefits.0.title'),
      content: t('pages.solutions.ai-consulting.why.benefits.0.content'),
      image: dashboardImage.src,
      icon: <Brain className="h-6 w-6 text-primary" />,
    },
    {
      id: 2,
      title: t('pages.solutions.ai-consulting.why.benefits.1.title'),
      content: t('pages.solutions.ai-consulting.why.benefits.1.content'),
      image: dashboardImage.src,
      icon: <LineChart className="h-6 w-6 text-primary" />,
    },
    {
      id: 3,
      title: t('pages.solutions.ai-consulting.why.benefits.2.title'),
      content: t('pages.solutions.ai-consulting.why.benefits.2.content'),
      image: dashboardImage.src,
      icon: <FileText className="h-6 w-6 text-primary" />,
    },
    {
      id: 4,
      title: t('pages.solutions.ai-consulting.why.benefits.3.title'),
      content: t('pages.solutions.ai-consulting.why.benefits.3.content'),
      image: dashboardImage.src,
      icon: <CircleGauge className="h-6 w-6 text-primary" />,
    },
    {
      id: 5,
      title: t('pages.solutions.ai-consulting.why.benefits.4.title'),
      content: t('pages.solutions.ai-consulting.why.benefits.4.content'),
      image: dashboardImage.src,
      icon: <BookOpenCheck className="h-6 w-6 text-primary" />,
    },
  ];

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    endX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = endX.current - startX.current;
    if (diff > SWIPE_THRESHOLD) {
      handlePrevious()
    } else if (diff < -SWIPE_THRESHOLD) {
      handleNext()
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    endX.current = e.clientX;
    const handleMouseMove = (e: MouseEvent) => {
      endX.current = e.clientX;
    };
    const handleMouseUp = () => {
      const diff = endX.current - startX.current;
      if (diff > SWIPE_THRESHOLD) {
        handlePrevious()
      } else if (diff < -SWIPE_THRESHOLD) {
        handleNext()
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Section
      id="why-ai-consulting"
      className="bg-muted/30"
      mainCta={{
        text: t('pages.solutions.ai-consulting.hero.cta.primary'),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      containerClassName=""
      largerTopGap
      secondaryCta={{
        text: t('pages.solutions.ai-consulting.hero.cta.secondary'),
        onClick: () => document.getElementById("ai-consulting-team")?.scrollIntoView({ behavior: "smooth" })
      }}
    >
      <div className="text-center px-6 mb-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t('pages.solutions.ai-consulting.why.title')}
        </motion.h2>
      </div>

      {/* Stat Callout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto mb-16 bg-muted/50 rounded-xl p-6 sm:p-8 relative mx-4 sm:mx-auto"
      >
        <div className="absolute left-6 top-6 text-4xl text-primary/20">
          &quot;
        </div>
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            onViewportEnter={() => setIsInView(true)}
            transition={{ type: "spring", stiffness: 100, damping: 10 }}
            className="text-7xl md:text-[12rem] font-bold text-primary mb-4 tracking-tight relative"
          >
            <motion.span
              className="absolute inset-0 blur-2xl bg-primary/20 rounded-full"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [0.9, 1.1, 0.9],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <span className="relative bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary to-primary/70">
              {percentage}%
            </span>
          </motion.div>
          <p className="text-xl text-muted-foreground">
            {t('pages.solutions.ai-consulting.why.stat.text')}
          </p>
          <div className="mt-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
            <span>{t('pages.solutions.ai-consulting.why.stat.source')}</span>
            <Link target="_blank" href="https://www.pwc.com/gx/en/issues/analytics/assets/pwc-ai-analysis-sizing-the-prize-report.pdf" className="inline-flex items-center hover:text-primary">
              <FileText className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Benefits Carousel - Optimized to only render active slide */}
      <div className="relative max-w-4xl mx-auto ">
        <div 
          className="w-full"
          role="region"
          aria-roledescription="carousel"
          aria-label="AI Consulting Benefits"
        >
          <div className="overflow-hidden   cursor-grab select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}>
            {/* Only render the active slide */}
            {data.map((item, index) => (
              index === activeSlide && (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="relative bg-background/50 hover:bg-background/80 transition-colors rounded-xl p-5 sm:p-8 border border-border/50 sm:mx-0"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className="flex justify-center sm:justify-start sm:mt-1">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          {item.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-4 text-center sm:text-left">{item.title}</h3>
                        <p className="text-base sm:text-lg text-muted-foreground max-md:text-center leading-relaxed">{item.content}</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )
            ))}
          </div>
          
          {/* Navigation controls with ARIA attributes */}
          <button 
            onClick={handlePrevious}
            className="absolute left-0 sm:left-12 lg:left-0 top-[99%] -translate-y-1/2 sm:-translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Previous slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          <button 
            onClick={handleNext}
            className="absolute right-0 lg:right-0 sm:right-12 top-[99%] -translate-y-1/2 sm:translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Next slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          
          {/* Slide indicator */}
          <div className="my-10  flex justify-center gap-2">
            <div className="flex gap-1">
              {data.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`w-2 h-2 rounded-full ${index === activeSlide ? 'bg-primary' : 'bg-primary/30'}`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeSlide ? 'true' : 'false'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Services() {
  return (
    <div id="services">
      <ServicesSection />
    </div>
  );
}

function Industries() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  const industryCategories = [
    { id: "financial", label: t('pages.solutions.ai-consulting.industries.categories.0.label'), icon: <Building2 className="h-4 w-4" /> },
    { id: "healthcare", label: t('pages.solutions.ai-consulting.industries.categories.1.label'), icon: <Hospital className="h-4 w-4" /> },
    { id: "retail", label: t('pages.solutions.ai-consulting.industries.categories.2.label'), icon: <ShoppingBag className="h-4 w-4" /> },
    { id: "manufacturing", label: t('pages.solutions.ai-consulting.industries.categories.3.label'), icon: <Factory className="h-4 w-4" /> },
    { id: "logistics", label: t('pages.solutions.ai-consulting.industries.categories.4.label'), icon: <Truck className="h-4 w-4" /> },
  ];

  const industryContents = {
    financial: {
      title: t('pages.solutions.ai-consulting.industries.contents.financial.title'),
      description: t('pages.solutions.ai-consulting.industries.contents.financial.description'),
      features: [
        t('pages.solutions.ai-consulting.industries.contents.financial.features.0'),
        t('pages.solutions.ai-consulting.industries.contents.financial.features.1'),
        t('pages.solutions.ai-consulting.industries.contents.financial.features.2'),
        t('pages.solutions.ai-consulting.industries.contents.financial.features.3')
      ],
      image: businessDiscussionImage.src,
      url: "https://savagescorp.ai/solutions/ai-consulting/financial"
    },
    healthcare: {
      title: t('pages.solutions.ai-consulting.industries.contents.healthcare.title'),
      description: t('pages.solutions.ai-consulting.industries.contents.healthcare.description'),
      features: [
        t('pages.solutions.ai-consulting.industries.contents.healthcare.features.0'),
        t('pages.solutions.ai-consulting.industries.contents.healthcare.features.1'),
        t('pages.solutions.ai-consulting.industries.contents.healthcare.features.2'),
        t('pages.solutions.ai-consulting.industries.contents.healthcare.features.3')
      ],
      image: scientistImage.src,
      url: "https://savagescorp.ai/solutions/ai-consulting/healthcare"
    },
    retail: {
      title: t('pages.solutions.ai-consulting.industries.contents.retail.title'),
      description: t('pages.solutions.ai-consulting.industries.contents.retail.description'),
      features: [
        t('pages.solutions.ai-consulting.industries.contents.retail.features.0'),
        t('pages.solutions.ai-consulting.industries.contents.retail.features.1'),
        t('pages.solutions.ai-consulting.industries.contents.retail.features.2'),
        t('pages.solutions.ai-consulting.industries.contents.retail.features.3')
      ],
      image: smartphoneOfficeImage.src,
      url: "https://savagescorp.ai/solutions/ai-consulting/retail"
    },
    manufacturing: {
      title: t('pages.solutions.ai-consulting.industries.contents.manufacturing.title'),
      description: t('pages.solutions.ai-consulting.industries.contents.manufacturing.description'),
      features: [
        t('pages.solutions.ai-consulting.industries.contents.manufacturing.features.0'),
        t('pages.solutions.ai-consulting.industries.contents.manufacturing.features.1'),
        t('pages.solutions.ai-consulting.industries.contents.manufacturing.features.2'),
        t('pages.solutions.ai-consulting.industries.contents.manufacturing.features.3')
      ],
      image: productionLineImage.src,
      url: "https://savagescorp.ai/solutions/ai-consulting/manufacturing"
    },
    logistics: {
      title: t('pages.solutions.ai-consulting.industries.contents.logistics.title'),
      description: t('pages.solutions.ai-consulting.industries.contents.logistics.description'),
      features: [
        t('pages.solutions.ai-consulting.industries.contents.logistics.features.0'),
        t('pages.solutions.ai-consulting.industries.contents.logistics.features.1'),
        t('pages.solutions.ai-consulting.industries.contents.logistics.features.2'),
        t('pages.solutions.ai-consulting.industries.contents.logistics.features.3')
      ],
      image: logisticsImage.src,
      url: "https://savagescorp.ai/solutions/ai-consulting/logistics"
    }
  };

  return (
      <TabSwitchSection
        title={t('pages.solutions.ai-consulting.industries.title')}
        subtitle={t('pages.solutions.ai-consulting.industries.subtitle')}
        categories={industryCategories}
        contents={industryContents}
        navigationStyles="lg:mb-24"
        defaultTab="financial"
        className="bg-muted/30"
      />
  );
}


import CaseStudy from "./case-study";
import { serializeJsonLd } from "@/lib/sanitize";

function FAQ() {
  const { t } = useTranslations();
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const faqs = [
    {
      id: "faq-1",
      question: t('pages.solutions.ai-consulting.faq.items.0.question'),
      answer: t('pages.solutions.ai-consulting.faq.items.0.answer'),
    },
    {
      id: "faq-2",
      question: t('pages.solutions.ai-consulting.faq.items.1.question'),
      answer: t('pages.solutions.ai-consulting.faq.items.1.answer'),
    },
    {
      id: "faq-3",
      question: t('pages.solutions.ai-consulting.faq.items.2.question'),
      answer: t('pages.solutions.ai-consulting.faq.items.2.answer'),
    },
    {
      id: "faq-4",
      question: t('pages.solutions.ai-consulting.faq.items.3.question'),
      answer: t('pages.solutions.ai-consulting.faq.items.3.answer'),
    },
  ];

  const toggleItem = (itemId: string) => {
    setActiveItem(prev => prev === itemId ? null : itemId);
  };

  return (
    <Section
      id="faq"
      title={t('pages.solutions.ai-consulting.faq.title')}
      subtitle={t('pages.solutions.ai-consulting.faq.subtitle')}
      className="bg-muted/30 max-md:!pt-8"
    >
      <div className="mx-auto md:max-w-[800px] lg:my-12">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col items-center justify-center space-y-2"
        >
          {faqs.map((faq, idx) => (
            <AccordionItem
              key={idx}
              value={faq.question}
              className="w-full border rounded-lg overflow-hidden shadow-lg hover:bg-primary/10 transition durantion-200 [&[data-state=open]]:bg-primary/10"
            >
              <AccordionTrigger className="px-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="px-4">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}

function FinalCTA() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <Section
      id="final-cta"
      className="bg-primary/5"
      mainCta={{
        text: t('pages.solutions.ai-consulting.hero.cta.primary'),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
    >
      <div className="text-center max-w-3xl mx-auto sm:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t('pages.solutions.ai-consulting.final-cta.title')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-muted-foreground mb-8"
        >
          {t('pages.solutions.ai-consulting.final-cta.description')}
        </motion.p>
      </div>
    </Section>
  );
}

export default function AIConsultingPage() {
  return (
    <main className="flex flex-col min-h-screen">
      <Hero />
      <WhyAIConsulting />
      <AIConsultingTeam />
      <Services />
      <Industries />
      <CaseStudy />
      <FAQ />
      <FinalCTA />

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SavagesCorp",
            "url": "https://savagescorp.ai",
            "logo": "https://savagescorp.ai/logo.png",
            "sameAs": [
              "https://twitter.com/savagescorp",
              "https://linkedin.com/company/savagescorp"
            ]
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Consulting Services",
            "provider": {
              "@type": "Organization",
              "name": "SavagesCorp"
            },
            "description": "Strategic AI consulting services to drive innovation, efficiency, and growth.",
            "areaServed": {
              "@type": "Country",
              "name": "Global"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://savagescorp.ai"
            },{
              "@type": "ListItem",
              "position": 2,
              "name": "Solutions",
              "item": "https://savagescorp.ai/solutions"
            },{
              "@type": "ListItem",
              "position": 3,
              "name": "AI Consulting",
              "item": "https://savagescorp.ai/solutions/ai-consulting"
            }]
          })
        }}
      />
    </main>
  );
}
